import { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';

interface Particle {
  id: number;
  x: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
  type: 'bean' | 'dust' | 'sparkle';
  color: string;
}

const COLORS = [
  '#C17817', // coffee-400 golden bronze
  '#D4A373', // coffee-300
  '#E6C9A8', // coffee-200
  '#A86815', // coffee-500
  '#F5E6D3', // coffee-100
  '#FFFFFF',
];

export default function CoffeeParticles() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const particles = useMemo<Particle[]>(() => {
    const list: Particle[] = [];
    // Main golden particles
    for (let i = 0; i < 20; i++) {
      list.push({
        id: i,
        x: Math.random() * 100,
        size: Math.random() * 8 + 4,
        duration: Math.random() * 10 + 8,
        delay: Math.random() * 8,
        opacity: Math.random() * 0.5 + 0.3,
        type: 'bean',
        color: COLORS[Math.floor(Math.random() * 4)],
      });
    }
    // Small dust particles
    for (let i = 20; i < 35; i++) {
      list.push({
        id: i,
        x: Math.random() * 100,
        size: Math.random() * 3 + 1,
        duration: Math.random() * 12 + 6,
        delay: Math.random() * 6,
        opacity: Math.random() * 0.4 + 0.2,
        type: 'dust',
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    }
    // Sparkles
    for (let i = 35; i < 45; i++) {
      list.push({
        id: i,
        x: Math.random() * 100,
        size: Math.random() * 4 + 2,
        duration: Math.random() * 6 + 4,
        delay: Math.random() * 10,
        opacity: Math.random() * 0.6 + 0.2,
        type: 'sparkle',
        color: '#F5E6D3',
      });
    }
    return list;
  }, []);

  if (!mounted) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[5]">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            left: `${p.x}%`,
            bottom: '-5%',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            opacity: p.opacity,
            boxShadow: p.type === 'sparkle' ? `0 0 ${p.size * 2}px ${p.color}` : 'none',
          }}
          animate={{
            y: [0, -(typeof window !== 'undefined' ? window.innerHeight * 0.7 : 600)],
            x: [
              0,
              (Math.random() - 0.5) * 60,
              (Math.random() - 0.5) * 30,
            ],
            opacity: [p.opacity, p.opacity * 0.8, 0],
            scale: [1, p.type === 'sparkle' ? 1.3 : 0.8, 0.5],
            rotate: p.type === 'bean' ? [0, 180, 360] : [0, 90, 0],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}

      {/* Ambient glow orbs */}
      {[0, 1, 2].map((i) => (
        <motion.div
          key={`orb-${i}`}
          className="absolute rounded-full"
          style={{
            width: 200 + i * 100,
            height: 200 + i * 100,
            background: `radial-gradient(circle, rgba(193,120,23,0.15) 0%, transparent 70%)`,
            filter: 'blur(40px)',
            left: `${20 + i * 30}%`,
            top: `${30 + i * 20}%`,
          }}
          animate={{
            x: [0, 30, -20, 0],
            y: [0, -20, 30, 0],
            scale: [1, 1.2, 0.9, 1],
          }}
          transition={{
            duration: 15 + i * 5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}
